import React, { useEffect, useRef, useState } from 'react';
import type { PdfColorMode } from '../../../types/fingering';

interface PdfDownloadSplitButtonProps {
  /** 現在選択中の PDF 出力色モード (issue #32)。 */
  pdfColorMode: PdfColorMode;
  /** 色ラジオ変更時に呼ばれる。色変更 = 再生成 (案 D-3) を発火する想定。 */
  onPdfColorModeChange: (mode: PdfColorMode) => void;
  /** 本体ボタン押下時の DL ハンドラ。 */
  onDownload: () => void;
  /** 本体ボタンに表示する結果ファイル名。 */
  fileName: string;
  /** 再生成中などで操作を止めたいとき true。 */
  disabled?: boolean;
}

const PdfDownloadSplitButton: React.FC<PdfDownloadSplitButtonProps> = ({
  pdfColorMode,
  onPdfColorModeChange,
  onDownload,
  fileName,
  disabled = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handlePointerDown = (e: PointerEvent) => {
      const container = containerRef.current;
      if (container && !container.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen]);

  const handleColorChange = (mode: PdfColorMode) => {
    setMenuOpen(false);
    onPdfColorModeChange(mode);
  };

  const handleRadioKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const menu = containerRef.current;
      if (!menu) return;
      const radios = menu.querySelectorAll<HTMLInputElement>('input[type="radio"]');
      const idx = Array.from(radios).indexOf(e.currentTarget);
      if (idx < 0) return;
      const nextIdx =
        e.key === 'ArrowDown'
          ? (idx + 1) % radios.length
          : (idx - 1 + radios.length) % radios.length;
      radios[nextIdx].focus();
    }
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="inline-flex w-full rounded-xl overflow-hidden border border-green-200/60 shadow-sm">
        <button
          type="button"
          onClick={onDownload}
          disabled={disabled}
          aria-label="運指付きPDFをダウンロード"
          className={`group flex items-center gap-3 p-3 sm:p-3.5 flex-1 min-w-0 bg-gradient-to-r from-green-50 to-emerald-50 transition-all active:scale-[0.98] ${
            disabled ? 'opacity-50 cursor-wait' : 'hover:shadow-md hover:shadow-green-100'
          }`}
        >
          <div className="w-10 h-10 rounded-lg bg-brand-green flex items-center justify-center flex-shrink-0 shadow-sm">
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="text-left flex-1 min-w-0">
            <div className="text-sm font-bold text-gray-700">運指付きPDF</div>
            <div className="text-xs text-gray-400 truncate">{fileName}</div>
          </div>
          <svg className="w-5 h-5 text-gray-300 group-hover:text-green-500 transition-colors flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
        </button>
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          disabled={disabled}
          aria-label="色を選択"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-controls="pdf-download-color-menu"
          className={`px-3 border-l border-green-200/60 bg-gradient-to-r from-emerald-50 to-green-100 text-gray-500 transition-all ${
            disabled ? 'opacity-50 cursor-wait' : 'hover:text-green-600 active:scale-[0.98]'
          }`}
        >
          <span aria-hidden="true">▼</span>
        </button>
      </div>

      {menuOpen && (
        <div
          role="menu"
          id="pdf-download-color-menu"
          aria-label="PDF 出力の色"
          className="absolute right-0 top-full mt-1 z-50 w-56 bg-white border border-gray-200 rounded-xl shadow-xl p-2"
        >
          <fieldset>
            <legend className="px-1 text-xs text-gray-500 mb-1">PDF の運指色</legend>
            <label className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer text-sm text-gray-700">
              <input
                type="radio"
                name="pdf-download-color-mode"
                value="color"
                checked={pdfColorMode === 'color'}
                onChange={() => handleColorChange('color')}
                onKeyDown={handleRadioKeyDown}
                className="accent-brand-green"
              />
              <span>カラー (弦別色)</span>
            </label>
            <label className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer text-sm text-gray-700">
              <input
                type="radio"
                name="pdf-download-color-mode"
                value="mono"
                checked={pdfColorMode === 'mono'}
                onChange={() => handleColorChange('mono')}
                onKeyDown={handleRadioKeyDown}
                className="accent-brand-green"
              />
              <span>白黒 (印刷向け)</span>
            </label>
          </fieldset>
          <p className="mt-1 px-1 text-[10px] leading-tight text-gray-400">
            色を変えると再生成します (数秒かかります)
          </p>
        </div>
      )}
    </div>
  );
};

export default PdfDownloadSplitButton;
